import React from 'react';
import BrandLogo from './BrandLogo';
import './GraphicDesignPage.css';

const gdpImages = {
  hero: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/Graphic-design-page/Hero%20Image.png',
  swiss: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/Graphic-design-page/12%20Column%20Swiss.png',
  asymettric: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/Graphic-design-page/Asymettric.png',
  hierarchical: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/Graphic-design-page/Hierarchical%20Tier.png',
};

const marqueeItems = [
  'Brand Strategy',
  'UI/UX Strategy',
  '3D Motion Graphics',
  'Social First Content',
  'Conversion Design',
  'Visual Storytelling',
  'Custom Typography',
  'Ad Creative Production',
];

const creativeLogicCards = [
  {
    id: '01',
    title: 'Brand Identity Systems',
    description:
      'Deep visual systems from logo to typography that keep your brand recognizable across all touchpoints.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
  },
  {
    id: '02',
    title: 'High-Conversion Ad Creative',
    description:
      'Scroll-stopping static and motion assets designed for performance campaigns and organic engagement.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M3 21l3.8-1 10.8-10.8a2.4 2.4 0 1 0-3.4-3.4L3.4 16.6 3 21z" />
      </svg>
    ),
  },
  {
    id: '03',
    title: 'Digital & UI Design',
    description:
      'Balanced interfaces where strong aesthetics and usability work together for modern digital products.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M3 14h4l3-9 4 14 3-8h4" />
      </svg>
    ),
  },
  {
    id: '04',
    title: 'Social Content Velocity',
    description:
      'High-quality visual assets at social speed for always-on posting across short-form channels.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
  },
];

const showcaseCards = [
  {
    title: 'The Minimalist - 12 Column Swiss',
    description:
      'A refined structure with serif typography and controlled spacing for luxury-style brands.',
    tag: 'TIMELESS . LUXURY . ELEGANT',
    image: gdpImages.swiss,
  },
  {
    title: 'The Bold Pop - Asymmetrical Grid',
    description:
      'High contrast colors and dynamic compositions designed to feel disruptive and attention-driven.',
    tag: 'DISRUPTIVE . GEN-Z . HIGH CONTRAST',
    image: gdpImages.asymettric,
  },
  {
    title: 'The Corporate Zen - Hierarchy Grid',
    description:
      'Clean and structured composition focused on trust, clarity, and information hierarchy.',
    tag: 'PROFESSIONAL . TRUST . FOCUSED',
    image: gdpImages.hierarchical,
  },
];

const philosophyCards = [
  {
    quote:
      'Design is the silent ambassador of a brand. Structure and rhythm create trust before the first sentence is read.',
    title: 'On Visual Trust',
  },
  {
    quote:
      'Great layouts feel invisible because they guide attention naturally from emotion to action.',
    title: 'On Hierarchy',
  },
  {
    quote:
      'A strong visual system is not trend-dependent. It scales from mobile screens to large-format campaigns.',
    title: 'On Scalability',
  },
];

export default function GraphicDesignPage() {
  return (
    <div className="gdp-page">
      <nav className="gdp-nav">
        <BrandLogo className="gdp-nav__logo" size="nav" />
        <ul className="gdp-nav__links">
          <li>
            <a href="#work">Style Showcase</a>
          </li>
          <li>
            <a href="#services">Creative Logic</a>
          </li>
          <li>
            <a href="#about">Our Story</a>
          </li>
        </ul>
        <a href="#contact" className="gdp-nav__cta">
          Contact
        </a>
      </nav>

      <section className="gdp-hero">
        <div className="gdp-hero__visual">
          <img
            src={gdpImages.hero}
            alt="Graphic design hero"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div className="gdp-hero__content">
          <p className="gdp-hero__eyebrow">Est. 2026 - London</p>
          <h1>
            Architects
            <br />
            <em>Of Visual</em>
            <br />
            Culture
          </h1>
          <p>
            BeyonDigital blends high-end art direction with performance design.
            We build visual identities that command attention and scale across channels.
          </p>
          <div className="gdp-hero__actions">
            <a href="#work" className="gdp-btn gdp-btn--dark">
              View Portfolio
            </a>
            <a href="#about" className="gdp-btn gdp-btn--ghost">
              Start your branding process ↗
            </a>
          </div>
          <div className="gdp-proof">
            <div>
              <strong>120+</strong>
              <span>Brand identities built</span>
            </div>
            <div>
              <strong>55%</strong>
              <span>Lift in brand recall</span>
            </div>
            <div>
              <strong>2 sec</strong>
              <span>First impression window</span>
            </div>
          </div>
        </div>
      </section>

      <section className="gdp-marquee">
        <div className="gdp-marquee__track">
          {[...marqueeItems, ...marqueeItems].map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              <span>{item}</span>
              <span className="gdp-marquee__dot">·</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="gdp-section" id="services">
        <header className="gdp-section__header">
          <h2>
            The <em>Creative Logic</em>
          </h2>
        </header>
        <div className="gdp-grid gdp-grid--services">
          {creativeLogicCards.map((card) => (
            <article className="gdp-service-card" key={card.title}>
              <p className="gdp-service-card__num">{card.id}</p>
              <div className="gdp-service-card__icon">{card.icon}</div>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="gdp-section" id="work">
        <header className="gdp-section__header gdp-section__header--showcase">
          <h2>
            Style Showcase:
            <br />
            <em>The Design Grid</em>
          </h2>
        </header>
        <div className="gdp-grid gdp-grid--showcase">
          {showcaseCards.map((card) => (
            <article className="gdp-showcase-card" key={card.title}>
              <div className="gdp-showcase-card__image">
                <img
                  src={card.image}
                  alt={card.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className="gdp-showcase-card__body">
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                <span>{card.tag}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="gdp-section" id="about">
        <header className="gdp-section__header gdp-section__header--philosophy">
          <h2>
            Design
            <br />
            <em>Philosophy</em>
          </h2>
        </header>
        <div className="gdp-grid gdp-grid--philosophy">
          {philosophyCards.map((card) => (
            <blockquote key={card.title} className="gdp-philosophy-card">
              <span className="gdp-philosophy-card__mark">"</span>
              <p>{card.quote}</p>
              <cite>{card.title}</cite>
            </blockquote>
          ))}
        </div>
      </section>

      <section className="gdp-cta" id="contact">
        <div className="gdp-cta__inner">
          <h2>
            Lets build your
            <br />
            <em>Brand's Blueprint.</em>
          </h2>
          <a href="mailto:sdevarsh54@beyondigital.co.uk" className="gdp-btn gdp-btn--dark">
            Book a design consultation
          </a>
        </div>
      </section>

      <footer className="gdp-footer">
        <div className="gdp-footer__top">
          <div className="gdp-footer__brand">
            <BrandLogo size="footer" asLink={false} variant="dark" />
            <p>Your brand deserves a structure that supports growth at every stage.</p>
          </div>

          <div className="gdp-footer__links">
            <div>
              <h4>Agency</h4>
              <a href="/">About</a>
              <a href="/">Work</a>
              <a href="/">Careers</a>
            </div>
            <div>
              <h4>Services</h4>
              <a href="/">Strategy</a>
              <a href="/">Content</a>
              <a href="/">Paid Social</a>
            </div>
            <div>
              <h4>Connect</h4>
              <a href="https://www.instagram.com/beyondigital.co/">Instagram</a>
              <a href="/">LinkedIn</a>
              <a href="/">Twitter/X</a>
            </div>
          </div>
        </div>
        <div className="gdp-footer__bottom">London - India - Dubai</div>
      </footer>
    </div>
  );
}
